import UnifiedLightboxModal, {
	LIGHTBOX_HASH,
} from '@components/unified-lightbox/Lightbox';
import { openUnifiedLightboxAtom } from '@components/unified-lightbox/atoms';
import { getFilterByMediaType } from '@components/unified-lightbox/helpers';
import { UnifiedLightboxTrackingEvents } from '@components/unified-lightbox/hooks/use-track-unified-lightbox';
import { useStorefrontEmptyStates } from '@feature-flags/hooks/Storefront/useStorefrontEmptyStates';

import { useIsMarkAsBookedLiteStorefrontsEnabled } from '@client/hooks/useMarkAsBookedLiteStorefronts';

import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { GridRow } from '@tkww/orion-web-ds-structure';
import noop from '@utils/noop';
import { useSetAtom } from 'jotai';
import React, {
	type FC,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import AnalyticsConstants from '../../../../../../constants/analytics';
import { afterNextPaint } from '../../../../../../helpers/after-next-paint';
import MediaNotAvailable from '../../../../../components/media_not_available';
import Media from '../../../components/Media';
import Carousel from '../../../components/carousel';
import SocialSharing from '../../../components/social_sharing';
import { MediaCTA } from '../../Gallery/components/MediaSummary/components/MediaCTA/MediaCTA';
import { ButtonRole } from '../../Gallery/components/MediaSummary/components/MediaCTA/constants';
import useTrackMediaAnalytics from '../../Gallery/containers/Media/hooks/use-track-media-analytics';
import BookVendor from '../../vendor_info/components/BookVendor';
import { useIsVendorBooked } from '../../vendor_info/components/BookVendor/hooks/use-is-vendor-booked';
import FavoritesButton from '../../vendor_info/components/favorites_button/FavoritesButton';
import Styles from './MobileGallery.scss';
import LandscapeGalleryView from './components/landscape_gallery_view/LandscapeGalleryView';
import { MobileGalleryQuickLinks } from './components/mobile_gallery_quick_links';
import { MobilePhotoCount } from './components/mobile_photo_count';
import useTrackCarouselInteraction from './hooks/use-track-carousel-interaction';

interface StateProps {
	membership: Redux.Membership;
	vendor: Vendor.Decorated;
}

interface OwnProps {
	trackingEvents?: UnifiedLightboxTrackingEvents;
}

type NewMobileGalleryProps = StateProps & OwnProps;

const NewMobileGallery: FC<NewMobileGalleryProps> = (props) => {
	const [activeImageIndex, setActiveImageIndex] = useState(0);
	const bookings = useAppSelector((state) => state.bookings.bookings);

	const location = useLocation();

	const { membership, vendor, trackingEvents } = props;
	const carouselMedia = vendor.mediaSummary.media || [];
	const trackCarouselInteraction = useTrackCarouselInteraction(vendor);
	const trackMediaAnalytics = useTrackMediaAnalytics(vendor);

	const [isUnifiedLightboxModalOpen, setIsUnifiedLightboxModalOpen] =
		useState<boolean>(false);

	const setOpenUnifiedLightbox = useSetAtom(openUnifiedLightboxAtom);
	const isEmptyStateEnabled = useStorefrontEmptyStates();
	const state = useAppSelector((state) => state);

	const isLiteStorefront = useLiteStorefront();
	const isVendorBooked = useIsVendorBooked(bookings, vendor.id);
	const isMarkAsBookedLiteStorefrontsEnabled =
		useIsMarkAsBookedLiteStorefrontsEnabled();
	const isBookedButtonEnabled =
		vendor.isPaid || isMarkAsBookedLiteStorefrontsEnabled;

	const showFavoriteButtonOnExperiment = useMemo(() => {
		return isBookedButtonEnabled ? !isVendorBooked : true;
	}, [isBookedButtonEnabled, isVendorBooked]);

	const onNavigationChange = useCallback(
		(index: number) => {
			setActiveImageIndex((prevState) => {
				if (trackingEvents?.onGalleryMediaSwipedCallback !== undefined) {
					trackingEvents.onGalleryMediaSwipedCallback({
						categoryName: carouselMedia[index].mediaType.toLowerCase(),
					});
				}
				trackCarouselInteraction(
					prevState < index ? 'swipe right' : 'swipe left',
					{
						mediaIndex: index,
						mediaType: carouselMedia?.[index].mediaType,
						item: carouselMedia?.[index].url,
					},
				);
				return index;
			});
		},
		[carouselMedia, trackCarouselInteraction, trackingEvents],
	);

	const formContext = useInlineRfqForm({
		initiator: 'photo gallery',
	});

	const vendorMediaList: string[] = carouselMedia
		.filter(
			(mediaItem, index, self) =>
				['PHOTO', 'VIDEO', '360TOUR'].includes(mediaItem.mediaType) &&
				self.findIndex((m) => m.mediaType === mediaItem.mediaType) === index,
		)
		.map((mediaItem) => {
			const mediaType =
				mediaItem.mediaType === '360TOUR' ? 'TOUR' : mediaItem.mediaType;
			return mediaType.toLowerCase();
		});

	const handleOpenModal = async () => {
		afterNextPaint(() => {
			if (!trackingEvents?.onGalleryOpenCallback) {
				trackMediaAnalytics(
					activeImageIndex,
					carouselMedia?.[activeImageIndex],
				);
			}

			setOpenUnifiedLightbox({
				indexInAllMedia: activeImageIndex,
				filter: getFilterByMediaType(
					carouselMedia?.[activeImageIndex].mediaType,
				),
				isEmptyStateEnabled,
				trackingEvents,
			});
			window.history.replaceState(null, '', `#${LIGHTBOX_HASH}`);
			setIsUnifiedLightboxModalOpen(true);
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: is fine
	useEffect(() => {
		if (location.search && location.hash === `#${LIGHTBOX_HASH}`) {
			const params = new URLSearchParams(location.search);
			const mediaIdParam = params.get('mediaId');

			if (mediaIdParam) {
				const index = carouselMedia.findIndex(
					(media) => media.id === mediaIdParam,
				);
				if (index !== -1) {
					setActiveImageIndex(index);
				}
				handleOpenModal();
			}
		}
	}, [location]);

	const decorated360Tours = useMemo(
		() =>
			carouselMedia
				.map((mediaItem, index) => ({ index, ...mediaItem }))
				.filter((mediaItem) => mediaItem.mediaType === '360TOUR'),
		[carouselMedia],
	);

	const decoratedVideo = useMemo(
		() =>
			carouselMedia
				.map((mediaItem, index) => ({ index, ...mediaItem }))
				.filter(
					(mediaItem) =>
						mediaItem.mediaType === 'VIDEO' ||
						mediaItem.mediaType === 'VIDEO_UPLOAD',
				),
		[carouselMedia],
	);

	const shouldShowQuickLinks = carouselMedia?.length > 1;

	const onLightBoxClose = () => {
		setIsUnifiedLightboxModalOpen(false);
	};

	const has360Tour = decorated360Tours.length > 0;
	const hasVideo = decoratedVideo.length > 0 && !isLiteStorefront;

	return (
		<>
			<div className={Styles.landscapeView}>
				<LandscapeGalleryView />
			</div>
			<div className={Styles.portraitView} data-mobilegallery="portrait">
				<GridRow hGutters={false}>
					<GridColumn xs="12">
						{has360Tour && (
							<div className={Styles.tourButtonContainer}>
								<MediaCTA
									mediaItems={decorated360Tours}
									singleButton={true}
									onClick={noop}
									role={ButtonRole.Tour}
									event={AnalyticsConstants.PLAY_TOUR}
									isBlockIcon={true}
								/>
							</div>
						)}
						{hasVideo && (
							<div
								className={Styles.videoButtonContainer}
								data-context={has360Tour ? 'right-button' : 'only-button'}
							>
								<MediaCTA
									mediaItems={decoratedVideo}
									singleButton={true}
									onClick={noop}
									role={ButtonRole.Video}
									event={AnalyticsConstants.SEE_VIDEOS}
									isBlockIcon={true}
								/>
							</div>
						)}
						<div className={Styles.buttonsContainer}>
							<SocialSharing vendor={vendor} icon="share-ios" />
							{showFavoriteButtonOnExperiment && (
								<FavoritesButton
									membership={membership}
									sourceContent="storefront carousel"
									transparent={false}
									vendor={vendor}
								/>
							)}
							{isBookedButtonEnabled && <BookVendor />}
						</div>
						<div
							className={`
								${Styles.mobileCarousel}
								${Styles.mobileAppCarousel}
								${hasVideo ? Styles.noPadding : ''}
							`}
							id="navPhotos"
							onClick={handleOpenModal}
							onKeyDown={handleOpenModal}
							role="button"
							tabIndex={0}
						>
							{vendor.mediaSummary.total > 1 && (
								<MobilePhotoCount
									currentIndex={activeImageIndex}
									totalNumber={vendor.mediaSummary.total}
								/>
							)}
							{carouselMedia && carouselMedia.length > 0 ? (
								<Carousel
									activeImageIndex={activeImageIndex}
									mediaLength={carouselMedia?.length || 0}
									renderNextButton={() => null}
									renderPreviousButton={() => null}
									onNavigationChange={onNavigationChange}
								>
									{carouselMedia.map((m, index) => (
										<Media
											lazy={index !== 0}
											mediaItem={m}
											width={450}
											height={250}
											key={m.id}
											imageClass={Styles.carouselMediaItem}
											preload={index === 0}
											smartCrop
										/>
									))}
								</Carousel>
							) : (
								<MediaNotAvailable />
							)}
						</div>
					</GridColumn>
				</GridRow>
				{shouldShowQuickLinks && (
					<MobileGalleryQuickLinks
						media={carouselMedia}
						context={formContext}
						trackingEvents={trackingEvents}
					/>
				)}
				{isUnifiedLightboxModalOpen && (
					<UnifiedLightboxModal
						entryPoint="StorefrontGallery"
						isModalOpen={isUnifiedLightboxModalOpen}
						closeCallback={onLightBoxClose}
						mediaId={carouselMedia?.[activeImageIndex]?.id}
					/>
				)}
			</div>
			<RfqModal context={formContext} />
		</>
	);
};

function mapStateToProps(state: Redux.State): StateProps {
	return {
		membership: state.membership,
		vendor: state.vendor.vendor as Vendor.Decorated,
	};
}

export default connect<StateProps, {}, OwnProps>(mapStateToProps)(
	NewMobileGallery,
);
